import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Mail, Lock, AlertCircle, Loader2 } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_1c07591d-63e3-4991-946f-f8a8e8c125c9/artifacts/i94v1fmk_IMG_0910.jpeg";

const LoginPage = () => {
  const { login, requestMagicLink } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [magicEmail, setMagicEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await login(email, password);
      navigate('/app/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await requestMagicLink(magicEmail);
      setMagicLinkSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al enviar el enlace');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-12">
            <Link to="/">
              <img src={LOGO_URL} alt="Silver Brick Development" className="h-16 mb-8" />
            </Link>
            <h1 className="heading-2 mb-2">{t('auth.login')}</h1>
            <p className="body-text">
              Acceda a su cuenta para continuar
            </p>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Tabs defaultValue="password" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-8">
              <TabsTrigger value="password">Contraseña</TabsTrigger>
              <TabsTrigger value="magic">Enlace Mágico</TabsTrigger>
            </TabsList>
            
            <TabsContent value="password">
              <form onSubmit={handleLogin} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('auth.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="nombre@empresa.com"
                      className="pl-10"
                      required
                      data-testid="login-email"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="password">{t('auth.password')}</Label>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10"
                      required
                      data-testid="login-password"
                    />
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full btn-primary"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    t('auth.login')
                  )}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="magic">
              {magicLinkSent ? (
                <div className="text-center py-8">
                  <Mail className="h-12 w-12 mx-auto mb-4 text-primary" strokeWidth={1} />
                  <h3 className="text-lg font-medium mb-2">Revise su correo</h3>
                  <p className="body-text text-sm">
                    Si existe una cuenta con ese correo, recibirá un enlace para iniciar sesión.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleMagicLink} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="magic-email">{t('auth.email')}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                      <Input
                        id="magic-email"
                        type="email"
                        value={magicEmail}
                        onChange={(e) => setMagicEmail(e.target.value)}
                        placeholder="nombre@empresa.com"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      t('auth.magicLink')
                    )}
                  </Button>
                </form>
              )}
            </TabsContent>
          </Tabs>

          <div className="mt-8">
          </div>

          <p className="mt-8 text-center text-sm text-muted-foreground">
            {t('auth.noAccount')}{' '}
            <Link to="/register" className="text-foreground hover:underline">
              {t('auth.register')}
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(https://images.unsplash.com/photo-1579281959510-26b99ae23ae3?q=80&w=2574&auto=format&fit=crop)`
          }}
        >
          <div className="absolute inset-0 bg-background/80" />
        </div>
        <div className="absolute inset-0 flex items-center justify-center p-16">
          <div className="max-w-lg text-center">
            <h2 className="heading-2 mb-6">
              Inversión Inmobiliaria de Clase Institucional
            </h2>
            <p className="body-text">
              Acceda a oportunidades cuidadosamente seleccionadas y estructuradas con disciplina y transparencia.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
